import { TrashIcon } from "@xola/icons";
import { Checkbox, flash, Modal, SubmitButton, Switch, Textarea } from "@xola/ui-kit";
import { FormField, InputField, SelectField } from "common";
import { FormikProps } from "formik";
import { get } from "lodash";
import React, { forwardRef, Ref, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { CheckInRule, Permission } from "types";
import { api } from "../../../api/api";
import { useSecrets, useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { getTicketPrinter, setTicketPrinter } from "../../../storages/PrinterStorage";
import { getDummyPurchase } from "../../../utils/getDummyPurchase";
import { getPrinters, print, PrintType } from "../../../utils/qzTray";
import { IsGranted } from "../../Common/IsGranted";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { generateTickets } from "../App/PurchaseCreate/CheckInAndPrintTicket";

interface TicketPrintingValues {
    autoCheckIn: boolean;
    autoPrint: boolean;
    enabled: boolean;
    ticket?: string;
    ticketPrinter: string;
    template: string;
    checkInRules?: CheckInRule[];
}

export type TicketPrintingFormRef = FormikProps<TicketPrintingValues>;

const defaultValue = {
    autoCheckIn: false,
    autoPrint: false,
    enabled: false,
    ticket: "",
    ticketPrinter: "",
    template: "",
};

export const TicketPrintingForm = forwardRef((props, ref: Ref<TicketPrintingFormRef>) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const values = seller?.preferences?.ticket ?? defaultValue;
    const checkInRules = values.checkInRules ?? [];
    const ticketPrinter = getTicketPrinter() ?? "";
    const [showAddRule, setShowAddRule] = useState(false);
    const secrets = useSecrets();
    const queryClient = useQueryClient();

    const { mutateAsync: updateTicketPreference, isLoading } = useMutation(async (payload: any) => {
        return api.put(`/users/${seller.id}/preferences/ticket`, payload);
    });

    const { data: printers = [] } = useQuery<string[]>(["printers", secrets], async () => {
        return getPrinters(secrets);
    });

    const handleSubmit = async (values: any) => {
        if (values.ticketPrinter?.length > 0) {
            setTicketPrinter(values.ticketPrinter);
        }

        await updateTicketPreference(values);

        await queryClient.invalidateQueries("account"); // Because `account` is not a collection or has ID
        flash.show({ text: "Preferences updated" });
        invalidate("sellers", seller.id);
    };

    const handlePrintTestTicket = async () => {
        const testPurchase = getDummyPurchase();
        const template = get(seller, "preferences.ticket.template");
        const { guests } = testPurchase.items[0];
        if (template && guests.length > 0) {
            const tickets = generateTickets([guests[0]], testPurchase, template, [guests[0].unit]);
            await print(tickets[0], [], PrintType.TICKET);
        } else {
            flash.warning("Please configure a ticket template");
        }
    };

    const handleAddRule = () => {
        setShowAddRule(!showAddRule);
    };

    return (
        <>
            <XolaFormik
                enableReinitialize
                innerRef={ref}
                initialValues={{ ...values, ticketPrinter }}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue }: any) => (
                    <div>
                        <fieldset className="space-y-4" disabled={isLoading}>
                            <IsGranted permissions={Permission.TICKET_PRINTING_VIEW}>
                                <FormField name="ticketPrinter" label="Ticket Printer" className="w-2/3">
                                    <SelectField>
                                        <option value="">Select a printer</option>
                                        {printers.map((printer) => (
                                            <option key={printer} value={printer} className="no-translate">
                                                {printer}
                                            </option>
                                        ))}
                                    </SelectField>
                                </FormField>
                            </IsGranted>

                            <IsGranted permissions={Permission.TICKET_PRINTING_SELLER_VIEW}>
                                <Checkbox
                                    label="Print ticket on successful purchase"
                                    checked={values.autoPrint}
                                    onChange={(e: any) => setFieldValue("autoPrint", e.target.checked)}
                                />

                                <Checkbox
                                    label="Automatically check-in guests when printing tickets"
                                    checked={values.autoCheckIn}
                                    onChange={(e: any) => setFieldValue("autoCheckIn", e.target.checked)}
                                />
                            </IsGranted>

                            <IsGranted permissions={Permission.TICKET_PRINTING_SELLER_VIEW}>
                                <FormField
                                    name="template"
                                    label="FGL Template"
                                    tooltip="Friendly Ghost Language template"
                                >
                                    <Textarea
                                        rows={6}
                                        value={values.template}
                                        className="font-mono"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            setFieldValue("template", e.target.value);
                                        }}
                                    />
                                </FormField>

                                <SecondaryButton size="small" onClick={handlePrintTestTicket}>
                                    Print Test Ticket
                                </SecondaryButton>
                            </IsGranted>

                            <IsGranted permissions={Permission.TICKET_PRINTING_SELLER_VIEW}>
                                <div>
                                    <div className="text-md font-bold">Check-In Rules</div>
                                    <div className="mb-2 py-2 text-gray-darker">
                                        {checkInRules.length === 0 ? (
                                            <span>
                                                Add rules to be validated during guest check-in. These rules can be
                                                configured to be printed on the ticket
                                            </span>
                                        ) : (
                                            <ul className="leading-6">
                                                {checkInRules.map((rule) => (
                                                    <li
                                                        key={rule.id}
                                                        className="hover:cursor-pointer"
                                                        data-testid="ticket-printing-form-item"
                                                    >
                                                        <CheckInRuleItem key={rule.id} rule={rule} />
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <SecondaryButton size="small" onClick={handleAddRule}>
                                        Add Rule
                                    </SecondaryButton>
                                </div>
                            </IsGranted>
                        </fieldset>
                    </div>
                )}
            </XolaFormik>

            <Modal isOpen={showAddRule} onClose={handleAddRule}>
                <ModalContent onClose={handleAddRule} />
            </Modal>
        </>
    );
});

const CheckInRuleItem = ({ rule }: { rule: CheckInRule }) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const queryClient = useQueryClient();
    const [showAddRule, setShowAddRule] = useState(false);
    const toggleEditRule = () => {
        setShowAddRule(!showAddRule);
    };

    const { mutateAsync: deleteCheckInRule } = useMutation(async () => {
        return api.delete(`/users/${seller.id}/preferences/ticket/checkInRules/${rule?.id}`); // This is always the seller ID
    });

    const handleTrash = async () => {
        // TODO: Replace confirm with generic modal. See X2-710
        /* eslint-disable-next-line no-alert */
        if (!confirm("Are you sure you want to delete this rule?")) {
            return;
        }

        await deleteCheckInRule();

        invalidate("sellers", seller.id);
        invalidate("users", seller.id);
        queryClient.invalidateQueries("account"); // Because `account` is not a collection or has ID
    };

    return (
        <span>
            <NoTranslate className="text-primary hover:underline" onClick={toggleEditRule}>
                {rule.name}
            </NoTranslate>
            <TrashIcon className="ml-2 hover:text-danger" onClick={handleTrash} />
            <Modal isOpen={showAddRule} onClose={toggleEditRule}>
                <ModalContent rule={rule} onClose={toggleEditRule} />
            </Modal>
        </span>
    );
};

const defaultCheckInRule = {
    name: "",
    required: false,
    showOnTicket: false,
    failText: "",
    passText: "",
};

const ModalContent = ({ rule, onClose }: { rule?: CheckInRule; onClose: () => void }) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const queryClient = useQueryClient();
    const ticketPrinter = getTicketPrinter() ?? "";
    const data = rule ?? defaultCheckInRule;

    const { mutateAsync: createCheckInRule, isLoading: isCreatingRule } = useMutation(async (payload: any) => {
        return api.post(`/users/${seller.id}/preferences/ticket/checkInRules`, payload); // This is always the seller ID
    });

    const { mutateAsync: updateCheckInRule, isLoading: isUpdatingRule } = useMutation(async (payload: any) => {
        return api.put(`/users/${seller.id}/preferences/ticket/checkInRules/${rule?.id}`, payload); // This is always the seller ID
    });

    const handleSubmit = async (values: any) => {
        await (rule?.id ? updateCheckInRule(values) : createCheckInRule(values));

        invalidate("sellers", seller.id);
        invalidate("users", seller.id);
        queryClient.invalidateQueries("account"); // Because `account` is not a collection or has ID

        onClose();
    };

    const isLoading = isCreatingRule || isUpdatingRule;

    return (
        <XolaFormik enableReinitialize initialValues={{ ...data, ticketPrinter }} onSubmit={handleSubmit}>
            {({ handleSubmit, values, setFieldValue }: any) => (
                <>
                    <Modal.Header>Add Check-in Rule</Modal.Header>
                    <Modal.Body className="space-y-4">
                        <FormField name="name" label="Title" className="mb-0">
                            <InputField placeholder="Name" />
                        </FormField>

                        <div>
                            <Switch.Group className="flex w-full items-center justify-between">
                                <Switch.Label direction="right" className="mr-3 ml-0">
                                    <span className="font-bold">Required</span>
                                </Switch.Label>

                                <Switch
                                    isChecked={values.required}
                                    data-testid="ticket-printing-form-required-toggle"
                                    size="medium"
                                    onChange={(value: boolean) => setFieldValue("required", value)}
                                />
                            </Switch.Group>
                        </div>

                        <div>
                            <Switch.Group className="flex w-full items-center justify-between">
                                <Switch.Label direction="right" className="mr-3 ml-0">
                                    <span className="font-bold">Show on ticket</span>
                                </Switch.Label>

                                <Switch
                                    isChecked={values.showOnTicket}
                                    data-testid="ticket-printing-form-show-ticket-toggle"
                                    size="medium"
                                    onChange={(value: boolean) => setFieldValue("showOnTicket", value)}
                                />
                            </Switch.Group>
                        </div>

                        {values.showOnTicket && (
                            <div>
                                <FormField name="passText" label="Pass Text">
                                    <InputField placeholder="Text" />
                                </FormField>
                                <FormField name="failText" label="Fail Text">
                                    <InputField placeholder="Text" />
                                </FormField>
                            </div>
                        )}
                    </Modal.Body>

                    <Modal.Footer className="space-x-4">
                        <SecondaryButton disabled={isLoading} onClick={onClose}>
                            Cancel
                        </SecondaryButton>

                        <SubmitButton isLoading={isLoading} onClick={handleSubmit}>
                            Save Changes
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};
